import type {
    FormDataV1PublicApiDto,
    MainGuestV1PublicApiDto,
    GuestV1PublicApiDto,
    VehicleDataV1ApiDto,
    PetDataV1ApiDto
} from "@/types/api";
import type {
    FormData,
    GuestData,
    VehicleData,
    PetData
} from "@/types/form";

import { IdTypeApiEnum } from "@/types/api";
import { PetType, Gender, VehicleType } from "@/types/enums";
import type { FormGuestEntry } from "@/types/guest";

/**
 * Maps a single API guest object (main or other) to our flat GuestData.
 */
function mapApiGuestToLocal(
    apiGuest: MainGuestV1PublicApiDto | GuestV1PublicApiDto,
    isPrimaryContact: boolean,
    entryId: string,
): GuestData {
    return {
        entryId,
        isPrimaryContact,
        guestNumId: apiGuest.guestNumId,
        firstName: apiGuest.firstName || "",
        middleName: apiGuest.middleName || "",
        lastName: apiGuest.lastName || "",
        // Story 20-2 Phase 1: when the API returns null (no gender committed yet on
        // the form_guests row), preserve emptiness — do NOT fabricate Gender.OTHER.
        // The OTHER fallback caused "Other" to appear in the dropdown for fresh guests
        // and defeated the form-empty rule in useGuestProfilePrefill.applyPrefill,
        // blocking gender prefill from the matched profile.
        gender: (apiGuest.gender ?? "") as "MALE" | "FEMALE" | "OTHER",
        idType: (apiGuest.idType ?? IdTypeApiEnum.PASSPORT) as "PASSPORT" | "NATIONAL_ID",
        id: apiGuest.id || "",
        idIssuingCountry: (apiGuest.idIssuingCountry ?? '') as GuestData['idIssuingCountry'],
        telephoneNumber: apiGuest.telephoneNumber || "",
        birthDate: apiGuest.birthDate || "",
        email: (apiGuest as MainGuestV1PublicApiDto).email || "",
        acceptTerms: apiGuest.acceptTerms ?? false,
        acceptTermsTimestamp: apiGuest.acceptTermsTimestamp,
        acceptPrivacy: apiGuest.acceptPrivacy ?? false,
        acceptPrivacyTimestamp: apiGuest.acceptPrivacyTimestamp,
        consentDataStorage: apiGuest.consentDataStorage ?? false,
        consentDataStorageTimestamp: apiGuest.consentDataStorageTimestamp,
        consentMarketing: apiGuest.consentMarketing ?? false,
        consentMarketingTimestamp: apiGuest.consentMarketingTimestamp,
        files: apiGuest.files
            ? apiGuest.files.map(f => ({
                uploaded: true,
                type: f.type as "ID_FRONT" | "ID_BACK" | "SELFIE" | "OTHER",
                guestNumId: apiGuest.guestNumId,
                guestEntryId: entryId,
                filename: f.url?.split('/').pop() || 'uploaded-file',
            }))
            : [],
        progress: apiGuest.progress,
    };
}

/**
 * Converts API data (FormDataV1PublicApiDto) into our flat FormData.
 *
 * Merges the API's mainGuest/otherGuests structure with relational entry UUIDs
 * to produce a single flat guests[] array.
 *
 * @param apiData - The form data from GET /forms/{publicId}
 * @param entries - Optional relational guest entries from GET /guests (provides UUIDs)
 */
export function convertApiFormDataToLocal(
    apiData: FormDataV1PublicApiDto,
    entries?: FormGuestEntry[],
): FormData {
    if (!apiData) {
        console.error('[convertApiFormDataToLocal] Received null or undefined apiData');
        throw new Error('Invalid API response: Data is missing');
    }

    const guests: GuestData[] = [];

    // Resolve entry UUIDs — match by position (main→isMainGuest, others→creation order)
    const mainEntry = entries?.find(e => e.isMainGuest);
    const nonMainEntries = entries
        ? [...entries].filter(e => !e.isMainGuest).sort((a, b) =>
            (a.createdAt || '').localeCompare(b.createdAt || ''))
        : [];

    // Map main guest
    if (apiData.mainGuest && (apiData.mainGuest.firstName || apiData.mainGuest.id || apiData.mainGuest.email)) {
        guests.push(mapApiGuestToLocal(
            apiData.mainGuest,
            true,
            mainEntry?.id || `legacy_main_${apiData.mainGuest.guestNumId}`,
        ));
    }

    // Map other guests
    (apiData.otherGuests || []).forEach((g, index) => {
        const matchedEntry = nonMainEntries[index];
        guests.push(mapApiGuestToLocal(
            g,
            false,
            matchedEntry?.id || `legacy_other_${g.guestNumId}`,
        ));
    });

    // Vehicles and pets
    const vehicles: VehicleData[] = (apiData.vehicles || []).map(v => ({
        type: (v.type as unknown as "CAR" | "MOTORCYCLE" | "BICYCLE" | "OTHER"),
        model: v.model,
        plateNr: v.plateNr,
        parkingSpace: v.parkingSpace,
        parkingRequested: v.parkingRequested,
    }));

    const pets: PetData[] = (apiData.pets || []).map(p => ({
        type: (p.type as unknown as "DOG" | "CAT" | "OTHER"),
        race: p.race,
    }));

    return { guests, vehicles, pets };
}

/**
 * Converts our flat FormData back into the API's mainGuest/otherGuests shape.
 * Used when saving form-level data (vehicles, pets).
 */
export function convertLocalFormDataToApi(localData: FormData): FormDataV1PublicApiDto {
    const primaryGuest = localData.guests.find(g => g.isPrimaryContact);
    const otherGuests = localData.guests.filter(g => !g.isPrimaryContact);

    const mapGuestToApi = (g: GuestData): GuestV1PublicApiDto => ({
        guestNumId: g.guestNumId,
        firstName: g.firstName,
        middleName: g.middleName,
        lastName: g.lastName,
        gender: g.gender as Gender,
        idType: g.idType as IdTypeApiEnum,
        id: g.id,
        idIssuingCountry: g.idIssuingCountry,
        telephoneNumber: g.telephoneNumber,
        email: g.email,
        birthDate: g.birthDate,
        files: [],
    });

    const mainGuest: MainGuestV1PublicApiDto = primaryGuest
        ? {
            ...mapGuestToApi(primaryGuest),
            email: primaryGuest.email || '',
        }
        : {
            guestNumId: 0,
            firstName: '',
            lastName: '',
            gender: Gender.OTHER,
            idType: IdTypeApiEnum.PASSPORT,
            id: '',
            idIssuingCountry: '' as MainGuestV1PublicApiDto['idIssuingCountry'],
            telephoneNumber: '',
            email: '',
            birthDate: '',
            files: [],
        };

    const vehicles: VehicleDataV1ApiDto[] = localData.vehicles.map(v => ({
        type: v.type as unknown as VehicleType,
        model: v.model,
        plateNr: v.plateNr,
        parkingSpace: v.parkingSpace,
    }));

    const pets: PetDataV1ApiDto[] = localData.pets.map(p => ({
        type: p.type as unknown as PetType,
        race: p.race,
    }));

    return {
        mainGuest,
        otherGuests: otherGuests.map(mapGuestToApi),
        vehicles,
        pets,
    };
}

/**
 * Helper: Find a guest by entryId in the flat guests array.
 */
export function findGuestByEntryId(guests: GuestData[], entryId: string): GuestData | undefined {
    return guests.find(g => g.entryId === entryId);
}

/**
 * Helper: Get the primary contact from guests array.
 */
export function getPrimaryContact(guests: GuestData[]): GuestData | undefined {
    return guests.find(g => g.isPrimaryContact);
}
